// Shared building blocks for the /docs sub-pages. Hand-coded React (not a
// markdown renderer) so each section can drop in real copy-button prompts,
// callouts, and live MCP URLs — same components used by Settings.

import { useState, type ReactNode } from "react";
import { MBIcon } from "../../components/mb";

export const MCP_URL = "https://mcpbuild.app/mcp";

// ── Page chrome ──────────────────────────────────────────────────────────────

export function DocPage({
  eyebrow,
  title,
  intro,
  children,
}: {
  eyebrow: string;
  title: string;
  intro: ReactNode;
  children: ReactNode;
}) {
  return (
    <article
      style={{
        maxWidth: 760,
        margin: "0 auto",
        padding: "48px 32px 96px",
        display: "flex",
        flexDirection: "column",
        gap: 28,
      }}
    >
      <header style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            letterSpacing: "0.12em",
            color: "var(--accent-fg)",
            textTransform: "uppercase",
          }}
        >
          {eyebrow}
        </span>
        <h1
          style={{
            margin: 0,
            fontFamily: "var(--font-sans)",
            fontSize: 36,
            lineHeight: "40px",
            fontWeight: 600,
            letterSpacing: "-0.02em",
            color: "var(--fg-1)",
          }}
        >
          {title}
        </h1>
        <div
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: 16,
            lineHeight: "26px",
            color: "var(--fg-2)",
          }}
        >
          {intro}
        </div>
      </header>
      {children}
    </article>
  );
}

export function DocSection({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section id={id} style={{ display: "flex", flexDirection: "column", gap: 14, scrollMarginTop: 80 }}>
      <h2
        style={{
          margin: 0,
          fontFamily: "var(--font-sans)",
          fontSize: 22,
          lineHeight: "28px",
          fontWeight: 600,
          letterSpacing: "-0.01em",
          color: "var(--fg-1)",
          paddingTop: 8,
          borderTop: "1px solid var(--line-1)",
        }}
      >
        {title}
      </h2>
      <div style={{ display: "flex", flexDirection: "column", gap: 14, fontFamily: "var(--font-sans)", fontSize: 15, lineHeight: "24px", color: "var(--fg-2)" }}>
        {children}
      </div>
    </section>
  );
}

// ── Prose helpers ────────────────────────────────────────────────────────────

export function P({ children }: { children: ReactNode }) {
  return <p style={{ margin: 0 }}>{children}</p>;
}

export function K({ children }: { children: ReactNode }) {
  return (
    <code
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 13,
        background: "var(--bg-1)",
        border: "1px solid var(--line-2)",
        padding: "1px 6px",
        borderRadius: 4,
        color: "var(--fg-1)",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </code>
  );
}

export function UL({ children }: { children: ReactNode }) {
  return (
    <ul style={{ margin: 0, paddingLeft: 20, display: "flex", flexDirection: "column", gap: 6 }}>
      {children}
    </ul>
  );
}

export function OL({ children }: { children: ReactNode }) {
  return (
    <ol style={{ margin: 0, paddingLeft: 20, display: "flex", flexDirection: "column", gap: 10 }}>
      {children}
    </ol>
  );
}

// ── Callouts ─────────────────────────────────────────────────────────────────

export function Callout({
  kind = "note",
  title,
  children,
}: {
  kind?: "note" | "warn" | "tip";
  title?: string;
  children: ReactNode;
}) {
  const palette = {
    note: { border: "var(--accent-line)", bg: "rgba(59,130,255,0.06)", fg: "var(--accent-fg)" },
    warn: { border: "rgba(245,158,11,0.45)", bg: "rgba(245,158,11,0.08)", fg: "#f59e0b" },
    tip: { border: "rgba(61,207,142,0.45)", bg: "rgba(61,207,142,0.08)", fg: "var(--success)" },
  }[kind];
  return (
    <div
      style={{
        border: `1px solid ${palette.border}`,
        background: palette.bg,
        borderRadius: 10,
        padding: "14px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      {title && (
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: palette.fg,
          }}
        >
          {title}
        </div>
      )}
      <div style={{ fontFamily: "var(--font-sans)", fontSize: 14, lineHeight: "22px", color: "var(--fg-1)" }}>
        {children}
      </div>
    </div>
  );
}

// ── Prompt card ──────────────────────────────────────────────────────────────
// Same shape as Settings.PromptCard so users see a familiar copy-paste
// affordance.

export function PromptCard({
  title,
  prompt,
  note,
}: {
  title: string;
  prompt: string;
  note?: ReactNode;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div
      style={{
        background: "var(--surface-1)",
        border: "1px solid var(--line-2)",
        borderRadius: 12,
        padding: 14,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ marginRight: "auto", fontFamily: "var(--font-sans)", fontSize: 13.5, fontWeight: 600, color: "var(--fg-1)" }}>
          {title}
        </div>
        <button
          onClick={() => {
            navigator.clipboard.writeText(prompt);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 10px",
            background: copied ? "var(--accent-soft)" : "var(--surface-2)",
            color: copied ? "var(--accent-fg)" : "var(--fg-1)",
            border: "1px solid var(--line-2)",
            borderRadius: 6,
            fontFamily: "var(--font-sans)",
            fontSize: 12,
            cursor: "pointer",
          }}
        >
          <MBIcon name={copied ? "check" : "copy"} size={12} />
          {copied ? "copied" : "copy"}
        </button>
      </div>
      <pre
        style={{
          margin: 0,
          padding: 10,
          background: "var(--bg-1)",
          border: "1px solid var(--line-2)",
          borderRadius: 8,
          color: "var(--fg-1)",
          fontFamily: "var(--font-mono)",
          fontSize: 12.5,
          lineHeight: "20px",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {prompt}
      </pre>
      {note && (
        <div style={{ fontFamily: "var(--font-sans)", fontSize: 12.5, color: "var(--fg-3)", lineHeight: "18px" }}>
          {note}
        </div>
      )}
    </div>
  );
}

// ── Inline code block (multi-line) ───────────────────────────────────────────

export function Code({ children }: { children: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <pre
        style={{
          margin: 0,
          padding: "12px 14px",
          background: "var(--bg-1)",
          border: "1px solid var(--line-2)",
          borderRadius: 8,
          color: "var(--fg-1)",
          fontFamily: "var(--font-mono)",
          fontSize: 12.5,
          lineHeight: "20px",
          overflowX: "auto",
        }}
      >
        {children}
      </pre>
      <button
        onClick={() => {
          navigator.clipboard.writeText(children);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          padding: "2px 8px",
          background: copied ? "var(--accent-soft)" : "var(--surface-2)",
          color: copied ? "var(--accent-fg)" : "var(--fg-3)",
          border: "1px solid var(--line-2)",
          borderRadius: 5,
          fontFamily: "var(--font-mono)",
          fontSize: 10.5,
          cursor: "pointer",
        }}
      >
        {copied ? "copied" : "copy"}
      </button>
    </div>
  );
}

// ── URL block (label + monospace URL + copy) ─────────────────────────────────

export function UrlBlock({ label, url }: { label: string; url: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--fg-4)" }}>
        {label}
      </div>
      <div style={{ display: "flex", alignItems: "stretch", background: "var(--bg-0)", border: "1px solid var(--line-2)", borderRadius: 6, overflow: "hidden" }}>
        <code style={{ flex: 1, padding: "10px 12px", fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--fg-1)", wordBreak: "break-all" }}>{url}</code>
        <button
          onClick={() => {
            navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
          style={{
            padding: "0 14px",
            border: 0,
            borderLeft: "1px solid var(--line-2)",
            background: copied ? "var(--accent-soft)" : "var(--surface-2)",
            color: copied ? "var(--accent-fg)" : "var(--fg-1)",
            fontFamily: "var(--font-sans)",
            fontSize: 12.5,
            fontWeight: 500,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <MBIcon name={copied ? "check" : "copy"} size={12} />
          {copied ? "copied" : "copy"}
        </button>
      </div>
    </div>
  );
}

// ── Next/prev page footer ────────────────────────────────────────────────────

export function PageFooter({
  prev,
  next,
}: {
  prev?: { to: string; label: string };
  next?: { to: string; label: string };
}) {
  return (
    <div
      style={{
        marginTop: 24,
        paddingTop: 24,
        borderTop: "1px solid var(--line-1)",
        display: "flex",
        gap: 12,
        flexWrap: "wrap",
      }}
    >
      {prev && (
        <a
          href={prev.to}
          style={{
            flex: 1,
            minWidth: 200,
            padding: "14px 16px",
            border: "1px solid var(--line-2)",
            borderRadius: 10,
            background: "var(--surface-1)",
            color: "var(--fg-1)",
            display: "flex",
            flexDirection: "column",
            gap: 4,
            textDecoration: "none",
          }}
        >
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--fg-4)" }}>
            ← Previous
          </span>
          <span style={{ fontFamily: "var(--font-sans)", fontSize: 14, fontWeight: 500 }}>{prev.label}</span>
        </a>
      )}
      {next && (
        <a
          href={next.to}
          style={{
            flex: 1,
            minWidth: 200,
            padding: "14px 16px",
            border: "1px solid var(--line-2)",
            borderRadius: 10,
            background: "var(--surface-1)",
            color: "var(--fg-1)",
            display: "flex",
            flexDirection: "column",
            gap: 4,
            textDecoration: "none",
            textAlign: "right",
          }}
        >
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--fg-4)" }}>
            Next →
          </span>
          <span style={{ fontFamily: "var(--font-sans)", fontSize: 14, fontWeight: 500 }}>{next.label}</span>
        </a>
      )}
    </div>
  );
}
